import { MoveToBucketDestinationFilter } from "@/models/Configuration";
import { BucketType } from "@/models/QueueItem";

// A move destination as accepted by POST IngestionQueue/Route. bucketType uses the shared
// BucketType enum (static/custom = 0, program = 1); both ids are null for the Unsorted destination.
export interface BucketDestination {
    bucketType: number | null;
    bucket: number | null;
}

export interface BucketOption {
    value: BucketDestination;
    text: string;
}

// Minimal shape of the program/bucket records the destination list is built from.
export interface NamedRecord {
    id: number;
    name: string;
}

// A static bucketType with a null bucket would persist as (0, null), which filters as a bucket of
// its own — this has to match the null pair usp_EScript_Add / usp_Image_Add already insert.
const UNSORTED_OPTION: BucketOption = {
    value: { bucketType: null, bucket: null },
    text: "Unsorted Bucket",
};

/**
 * Builds the destination options for the processing-screen "Move to Bucket" action, applying the
 * tenant's configured destination filter.
 *  - "All": Unsorted, then program buckets, then static (non-program) buckets
 *  - "ProgramOnly": program buckets only
 *  - "NonProgramOnly": Unsorted, then static (non-program) buckets
 *
 * Unsorted is a non-program destination, so it is offered for "All" and "NonProgramOnly" but not
 * "ProgramOnly".
 */
export function buildMoveToBucketOptions(
    programs: NamedRecord[],
    staticBuckets: NamedRecord[],
    filter: MoveToBucketDestinationFilter
): BucketOption[] {
    const programOptions: BucketOption[] = programs.map(p => ({
        value: { bucketType: BucketType.Program, bucket: p.id },
        text: p.name,
    }));

    const staticOptions: BucketOption[] = staticBuckets.map(b => ({
        value: { bucketType: BucketType.Static, bucket: b.id },
        text: b.name,
    }));

    switch (filter) {
        case "ProgramOnly":
            return programOptions;
        case "NonProgramOnly":
            return [UNSORTED_OPTION, ...staticOptions];
        case "All":
        default:
            return [UNSORTED_OPTION, ...programOptions, ...staticOptions];
    }
}
